import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../components/DataGrid/DataGrid';
import PermissionGuard from '../../components/PermissionGuard';
import { useAuth } from '../../context/AuthContext';
import { Permissions } from '../../constants/Permissions';
import { roleService } from '../../services/roleService';
import { UserRole } from '../../types';
import '../Products/ProductCategoryListPage.css';
import { CirclePlus, Eye, RefreshCw, SquarePen, Trash2 } from 'lucide-react';

const RoleListPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(
    (location.state as { message?: string } | null)?.message ?? null
  );

  useEffect(() => {
    loadRoles();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const loadRoles = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await roleService.getRoles();
      const data = response?.data?.data;
      setRoles(Array.isArray(data) ? data : []);
    } catch {
      setError(t('role.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const response = await roleService.getRoles();
      const data = response?.data?.data;
      setRoles(Array.isArray(data) ? data : []);
    } catch {
      setError(t('role.failedToLoad'));
    } finally {
      setRefreshing(false);
    }
  };

  const columns: GridColumn[] = [
    {
      key: 'name',
      label: t('role.roleName'),
      sortable: true,
      width: '180px',
      render: (value: string) => <strong>{value}</strong>,
    },
    {
      key: 'description',
      label: t('role.description'),
      sortable: false,
      render: (value: string) => (
        <span style={{ color: '#6b7280', fontSize: '13px' }}>{value || '—'}</span>
      ),
    },
    {
      key: 'permissions',
      label: t('role.permissionsCount'),
      sortable: false,
      width: '120px',
      hideMobile: true,
      render: (_value, item: UserRole) => (
        <span className="badge badge-info" style={{ fontSize: '12px' }}>
          {item.permissions?.length ?? 0} {t('role.permissions').toLowerCase()}
        </span>
      ),
    },
    {
      key: 'isSystem',
      label: t('role.type'),
      sortable: false,
      width: '100px',
      hideMobile: true,
      render: (value: boolean) => (
        <span className={`badge ${value ? 'badge-draft' : 'badge-active'}`}>
          {value ? t('role.systemRole') : t('role.customRole')}
        </span>
      ),
    },
  ];

  const actions: GridAction[] = [
    {
      icon: (
        <Eye size={16} strokeWidth={1.75} />
      ),
      onClick: (item: UserRole) => navigate(`/roles/${item.id}`),
      className: 'grid-action-btn-view',
      title: t('common.view'),
    },
    ...(hasPermission(Permissions.Roles.Edit)
      ? [{
          icon: (
            <SquarePen size={16} strokeWidth={1.75} />
          ),
          onClick: (item: UserRole) => navigate(`/roles/${item.id}/edit`),
          className: 'grid-action-btn-edit',
          title: t('common.edit'),
          hidden: (item: UserRole) => item.isSystem,
        }]
      : []),
    ...(hasPermission(Permissions.Roles.Delete)
      ? [{
          icon: (
            <Trash2 size={16} strokeWidth={1.75} />
          ),
          onClick: (item: UserRole) =>
            navigate(`/roles/${item.id}/delete`, { state: { roleName: item.name } }),
          className: 'grid-action-btn-delete',
          title: t('common.delete'),
          hidden: (item: UserRole) => item.isSystem,
        }]
      : []),
  ];

  return (
    <Layout>
      <div className="container">
        <div className="header">
          <h1 className="title">{t('role.title')}</h1>
          <div className="header-actions">
            <PermissionGuard claim={Permissions.Roles.Create}>
              <button className="btn-new" onClick={() => navigate('/roles/create')}>
                <CirclePlus/>
                {t('role.newRole')}
              </button>
            </PermissionGuard>
            <button
              className="btn-refresh"
              onClick={handleRefresh}
              disabled={refreshing}
              title={t('role.refreshRoles')}
            >
              <RefreshCw size={18} strokeWidth={2} className={refreshing ? 'refresh-icon-spinning' : ''} />
              {refreshing ? t('common.refreshing') : t('role.refreshRoles')}
            </button>
          </div>
        </div>

        {successMessage && (
          <div className="alert alert-success alert-dismissible fade show">
            <strong>{t('common.success')}:</strong> {successMessage}
            <button type="button" className="btn-close" onClick={() => setSuccessMessage(null)} />
          </div>
        )}

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            <strong>{t('common.error')}:</strong> {error}
            <button type="button" className="btn-close" onClick={() => setError(null)} />
          </div>
        )}

        <DataGrid
          data={roles}
          columns={columns}
          actions={actions}
          loading={loading}
          pageSize={10}
          searchPlaceholder={t('common.search')}
          emptyMessage={t('role.noRolesFound')}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </div>
    </Layout>
  );
};

export default RoleListPage;
